if (!customElements.get('gms-pack-picker')) {
  customElements.define('gms-pack-picker', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) {
        window.addEventListener('pageshow', this.restorePage);
        document.addEventListener('visibilitychange', this.refreshVisibleCart);
        this.observePurchase();
        this.syncCart();
        return;
      }
      this.ready = true;
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-gms-add]');
      this.buttons = this.querySelectorAll('[data-gms-add], [data-gms-sticky-add]');
      this.sticky = this.querySelector('[data-gms-sticky]');
      this.error = this.querySelector('[data-gms-error]');
      this.options = this.querySelector('fieldset');
      this.busy = false;
      this.cartCount = Number(this.dataset.cartCount) || 0;
      this.cartKnown = true;
      this.cartRequest = 0;
      this.restorePage = event => {
        if (!event.persisted) return;
        this.busy = false;
        this.error.hidden = true;
        this.refresh();
        this.syncCart();
      };
      this.refreshVisibleCart = () => {
        if (document.visibilityState === 'visible' && !this.busy) this.syncCart();
      };
      window.addEventListener('pageshow', this.restorePage);
      document.addEventListener('visibilitychange', this.refreshVisibleCart);
      const requested = new URLSearchParams(location.search).get('pack');
      if (['single', 'pair'].includes(requested)) {
        const radio = this.querySelector(`input[data-pack="${requested}"]`);
        if (radio && !radio.disabled) radio.checked = true;
      }
      this.addEventListener('change', () => {
        this.refresh();
        this.syncGallery();
      });
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        this.addSelection();
      });
      this.refresh();
      this.observePurchase();
      this.syncCart();
      customElements.whenDefined('gms-feature-tour').then(() => this.syncGallery());
    }
    disconnectedCallback() {
      window.removeEventListener('pageshow', this.restorePage);
      document.removeEventListener('visibilitychange', this.refreshVisibleCart);
      this.cartRequest++;
      this.purchaseObserver?.disconnect();
    }
    observePurchase() {
      if (!this.sticky || !('IntersectionObserver' in window)) return;
      this.purchaseObserver?.disconnect();
      this.purchaseObserver = new IntersectionObserver(([entry]) => {
        this.sticky.hidden = entry.isIntersecting;
      });
      this.purchaseObserver.observe(this.button);
    }
    async syncCart() {
      const request = ++this.cartRequest;
      this.cartChecking = true;
      this.refresh();
      const root = window.Shopify?.routes?.root || '/';
      try {
        const response = await fetch(`${root}cart.js`, {headers: {'Accept': 'application/json'}, cache: 'no-store'});
        if (!response.ok) throw new Error('cart-read-failed');
        const cart = await response.json();
        if (request !== this.cartRequest || this.busy) return;
        this.cartCount = cart.items.reduce((count, item) => count + (String(item.product_id) === this.dataset.product ? item.quantity : 0), 0);
        this.cartKnown = true;
      } catch {
        if (request !== this.cartRequest || this.busy) return;
        this.cartKnown = false;
      }
      this.cartChecking = false;
      this.refresh();
    }
    selectedPack() { return this.querySelector('input:checked')?.dataset.pack || 'single'; }
    canBuy() {
      return this.dataset.launchReady === 'true' && this.dataset.clockAvailable === 'true' &&
        (this.selectedPack() === 'single' || this.dataset.bundlesReady === 'true');
    }
    syncGallery() {
      const kind = this.selectedPack() === 'pair' ? 'pair' : 'studio';
      this.closest('.gms-product')?.querySelector(`[data-gms-gallery="${kind}"]`)?.click();
    }
    refresh() {
      const container = this.closest('.gms-product');
      const single = this.cartCount === 1 ? this.dataset.singleSavingPrice : this.dataset.singlePrice;
      const pair = this.cartCount >= 2 ? this.dataset.pairFullPrice : this.dataset.pairPrice;
      const prices = {single, pair};
      this.querySelectorAll('input[data-pack]').forEach(input => {
        input.dataset.price = prices[input.dataset.pack];
      });
      this.querySelectorAll('[data-gms-pack-price]').forEach(element => {
        element.textContent = prices[element.dataset.gmsPackPrice];
      });
      const saving = this.querySelector('[data-gms-pair-saving]');
      if (saving) saving.hidden = this.cartCount >= 2;
      const note = this.querySelector('[data-gms-cart-note]');
      if (note) {
        note.hidden = this.cartKnown && this.cartCount === 0;
        note.textContent = !this.cartKnown ? this.dataset.cartUnknown : this.cartCount === 1 ? this.dataset.cartOne : this.dataset.cartPair;
      }
      const selected = this.querySelector('input:checked');
      const price = container?.querySelector('[data-gms-selection-price]');
      if (price && selected?.dataset.price) {
        price.textContent = selected.dataset.price;
        price.closest('.gms-price-line').hidden = !this.cartKnown;
      }
      const context = container?.querySelector('[data-gms-price-context]');
      if (context) context.textContent = this.cartCount > 0 ? this.selectedPack() === 'pair' ? this.dataset.additionalPair : this.dataset.additionalSingle : this.dataset.oneTime;
      this.buttons.forEach(button => { button.disabled = this.busy || this.cartChecking || !this.canBuy(); });
      if (this.options) this.options.disabled = this.busy;
      const template = this.selectedPack() === 'pair' ? this.dataset.addPair : this.dataset.addSingle;
      const plain = this.selectedPack() === 'pair' ? this.dataset.addPairPlain : this.dataset.addSinglePlain;
      const label = this.cartKnown ? template?.replace('__PRICE__', prices[this.selectedPack()]) : plain;
      const buttonLabel = this.busy ? this.dataset.wait : this.cartChecking ? this.dataset.cartChecking : this.canBuy() ? label || this.dataset.add : this.dataset.unavailable;
      this.buttons.forEach(button => { button.textContent = buttonLabel; });
      const stickySelection = this.querySelector('[data-gms-sticky-selection]');
      if (stickySelection) stickySelection.textContent = this.selectedPack() === 'pair' ? this.dataset.pairLabel : this.dataset.singleLabel;
    }
    async addSelection() {
      if (this.busy || this.cartChecking || !this.canBuy()) return;
      this.busy = true;
      this.error.hidden = true;
      this.refresh();
      const items = [{id: this.dataset.clock, quantity: this.selectedPack() === 'pair' ? 2 : 1}];
      const root = window.Shopify?.routes?.root || '/';
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST', headers: {'Content-Type':'application/json','Accept':'application/json'},
          body: JSON.stringify({items})
        });
        if (!response.ok) throw new Error('cart-add-failed');
        await response.json();
        location.assign(`${root}cart`);
      } catch {
        this.error.textContent = this.dataset.error;
        this.error.hidden = false;
        this.error.focus();
        this.busy = false;
        this.refresh();
      }
    }
  });
}

